/** Service routes — SEO slugs, meta, and long-form blocks for detail pages */

export const servicesCatalog = [
  {
    slug: "custom-software-development",
    title: "Custom Software Development",
    shortTitle: "Custom Software",
    metaTitle: "Custom Software Development for Startups & SMBs | Dexton Technology",
    metaDescription:
      "Architecture-first builds for SaaS, internal tools, and product platforms—with milestone delivery, pragmatic testing, documentation, and remote collaboration for global teams.",
    keywords:
      "custom software development, hire remote software developers, product engineering agency, MVP development company worldwide",
    bullets: ["Architecture & codebase health", "Milestone-based delivery", "Docs your team can onboard with"],
    lead:
      "We turn fuzzy requirements into phased releases: clear backlog, demos, assumptions called out upfront, and an engineering roadmap stakeholders can approve—without drowning in jargon.",
    audiences: ["Startups shipping v1+", "SMBs replacing spreadsheets", "SaaS teams extending core apps"],
    offerings: [
      { title: "Discovery & backlog shaping", detail: "User flows, edge cases, and release slices so estimates mean something." },
      { title: "Application engineering", detail: "Web + API layers aligned to your scalability and compliance needs." },
      { title: "Launch & transfer", detail: "Runbooks, handoff calls, and optional embed with your team." },
    ],
    engagements: ["Fixed-scope phases", "Time & materials with caps", "Dedicated squad after fit is proven"],
    pillTags: ["React", "Next.js", "TypeScript", "Node.js", "Python"],
    relatedSlugs: ["saas-development", "web-development-react", "mobile-app-development"],
  },
  {
    slug: "web-development-react",
    title: "Web Development",
    shortTitle: "Web Development",
    metaTitle: "Web Development Agency | React & Next.js | Conversion-Ready UX | Dexton",
    metaDescription:
      "Landing pages to complex web apps—performance-focused builds with conversion-led UX patterns, resilient component architecture, Core Web Vitals awareness, and clean handoff.",
    keywords:
      "web development agency, React developer remote, Next.js agency global, SaaS web app frontend, redesign web application",
    bullets: ["Component-driven frontend", "API integration & SSR patterns", "Performance & accessibility pushes"],
    lead:
      "Whether you’re shipping a campaign site or a mission-critical SaaS frontend, we build pages that stay fast under real traffic—with patterns your future engineers won’t resent.",
    audiences: ["SaaS marketing + app surfaces", "E-commerce adjacent brands", "Agencies outsourcing UI builds"],
    offerings: [
      { title: "Marketing & funnel pages", detail: "Message match, headline tests, friction removal on forms." },
      { title: "Product web apps", detail: "State, auth, dashboards, workflows—wired to your APIs." },
      { title: "Refactors & speed ups", detail: "Bundle diet, lazy loading, critical path CSS, regression-safe rollouts." },
    ],
    engagements: ["Sprint-based UI delivery", "Paired roadmap with backend team", "White-label builds for studios"],
    pillTags: ["React", "Next.js", "Tailwind CSS", "TypeScript", "Core Web Vitals"],
    relatedSlugs: ["ui-ux-product-design", "custom-software-development", "saas-development"],
  },
  {
    slug: "mobile-app-development",
    title: "Mobile App Development",
    shortTitle: "Mobile Apps",
    metaTitle: "Mobile App Development | React Native & Flutter | Dexton Technology",
    metaDescription:
      "Production-minded iOS/Android apps—analytics, crashes, pushes, builds, release notes, and pragmatic UX for onboarding and retention. Remote delivery for founders and agencies.",
    keywords:
      "mobile app developer remote, React Native agency, Flutter app development MVP, outsource mobile developers worldwide",
    bullets: ["MVP-friendly cross-platform stacks", "Store submission guidance", "Analytics & crash tooling hooks"],
    lead:
      "We focus on installs that survive review, UX that survives real users, and builds that survive v2—we don’t stop at screenshot demos.",
    audiences: ["Founders validating mobile MVP", "SaaS adding native companion", "Brands upgrading legacy apps"],
    offerings: [
      { title: "Product flows & navigation", detail: "Auth, deep links, offline-aware patterns where needed." },
      { title: "Native-adjacent polish", detail: "Motion, haptics, platform conventions where they matter." },
      { title: "Release engineering", detail: "CI builds, env separation, beta tracks, store metadata." },
    ],
    engagements: ["Milestone MVP", "Feature squads", "Rescue & hardening pass"],
    pillTags: ["React Native", "Flutter", "Swift", "Kotlin", "Push & analytics"],
    relatedSlugs: ["custom-software-development", "web-development-react", "saas-development"],
  },
  {
    slug: "saas-development",
    title: "SaaS Development",
    shortTitle: "SaaS",
    metaTitle: "SaaS Development Company | Multi-Tenant MVPs | Startups | Dexton",
    metaDescription:
      "Multi-tenant MVPs through growth-stage features—billing-friendly architecture, pragmatic permissions, onboarding UX, integrations, APIs, admin tooling, and release cadence for remote teams.",
    keywords:
      "SaaS development company, SaaS MVP development, multi-tenant architecture, outsource SaaS engineers remote",
    bullets: ["Tenancy-ready patterns early", "Admin + permissions that scale", "APIs partners can integrate with"],
    lead:
      "Early SaaS decisions echo for years—we help founders avoid rewriting auth, billing, and roles twelve months later by choosing the smallest architecture that survives real customers.",
    audiences: ["Seed–Series founders", "SMBs productizing workflows", "Agencies subcontracting SaaS UI/API"],
    offerings: [
      { title: "Accounts, roles, audit trail", detail: "RBAC patterns and admin visibility without endless custom code." },
      { title: "Billing & plans", detail: "Stripe/Paddle-friendly flows; usage vs seat models discussed honestly." },
      { title: "Integrations", detail: "Webhooks, OAuth apps, exports—what your GTM team will actually sell." },
    ],
    engagements: ["Phased MVP → v1", "Dedicated pod after discovery", "Feature factory with weekly demos"],
    pillTags: ["Multi-tenant", "Stripe / billing", "APIs", "Admin panels", "Webhooks"],
    relatedSlugs: ["custom-software-development", "web-development-react", "ai-integrations"],
  },
  {
    slug: "ui-ux-product-design",
    title: "UI/UX & Product Design",
    shortTitle: "UI/UX Design",
    metaTitle: "UI/UX Design Services | Product UX for B2B SaaS | Dexton",
    metaDescription:
      "Conversion-led UX, design systems, and dev-ready handoff—flows, wireframes, high-fidelity UI, microcopy for forms, and patterns that speed engineering without thrash.",
    keywords:
      "UI UX design agency B2B, SaaS product design, design system Figma handoff, web app UX audit",
    bullets: ["Journey maps & flows", "Component libraries aligned to frontend", "Usability-informed iteration"],
    lead:
      "Design should reduce engineering thrash—we pair UX clarity with specs engineers can estimate, so timelines stop slipping on ‘small visual tweaks.’",
    audiences: ["SaaS rebranding funnel + app", "Founders needing design before build", "Agencies bridging design gaps"],
    offerings: [
      { title: "UX structure", detail: "Information architecture and primary flows prioritized by revenue/risk." },
      { title: "UI systems", detail: "Tokens/components that map to implementation reality." },
      { title: "Launch polish", detail: "Empty/error states, onboarding, transactional microcopy." },
    ],
    engagements: ["Vision sprint packs", "Ongoing UX/UI retainer alongside build", "Pre-build-only packages"],
    pillTags: ["Figma systems", "Prototypes", "Accessibility", "B2B forms", "Handoff QA"],
    relatedSlugs: ["web-development-react", "saas-development", "custom-software-development"],
  },
  {
    slug: "ai-integrations",
    title: "AI Solutions & Integrations",
    shortTitle: "AI",
    metaTitle: "AI Integration for SaaS & Workflows | LLM Features | Dexton",
    metaDescription:
      "Ship AI features that survive prod—RAG patterns, agent-shaped workflows, human-in-the-loop UX, cost/latency guardrails, and integration with your existing auth and data policies.",
    keywords:
      "AI integration developer, LLM SaaS features, OpenAI API integration, intelligent automation B2B",
    bullets: ["Grounded retrieval where needed", "Safety & logging patterns", "UX that sets expectations clearly"],
    lead:
      "Demos are easy; production is not—we help you avoid leaking data, burning token budgets, or shipping chat boxes users distrust.",
    audiences: ["SaaS adding copilots", "Ops teams automating manual review", "Founders testing AI wedge features"],
    offerings: [
      { title: "Use-case design", detail: "When to use gen vs rules vs search—so you don’t ‘LLM everything.’" },
      { title: "Implementation", detail: "Prompting, tools, eval hooks, fallbacks, monitoring story." },
      { title: "Rollout", detail: "Feature flags, sampling, admin kill-switches, red-team basics." },
    ],
    engagements: ["2–3 week AI spike", "Phased productization", "Audit + remediate existing AI path"],
    pillTags: ["OpenAI / LLM APIs", "RAG", "Workflow automation", "Embeddings", "Guardrails"],
    relatedSlugs: ["saas-development", "custom-software-development", "cloud-devops"],
  },
  {
    slug: "cloud-devops",
    title: "Cloud & DevOps",
    shortTitle: "Cloud / DevOps",
    metaTitle: "Cloud & DevOps Services | CI/CD | AWS Azure GCP | Dexton",
    metaDescription:
      "Pragmatic cloud setup for lean teams—CI/CD, environments, secrets hygiene, observability basics, and cost-aware scaling choices so releases stay boring (in a good way).",
    keywords:
      "DevOps consulting remote, CI/CD setup, AWS deployment, Docker Kubernetes support, cloud cost optimization",
    bullets: ["Pipeline & env discipline", "Observability you’ll actually read", "Security hygiene for lean teams"],
    lead:
      "We don’t sell slide-deck ‘DevOps transformation’—we wire the minimum reliable path from commit to production for your risk profile.",
    audiences: ["Teams outgrowing manual deploys", "Founders pre-SOC2 hygiene", "Agencies standardizing client stacks"],
    offerings: [
      { title: "Foundations", detail: "Accounts, IAM least-privilege, secrets, staging parity." },
      { title: "Delivery", detail: "CI test gates, deploy strategies, rollback story." },
      { title: "Ops visibility", detail: "Logs, metrics, alerts that page humans on real issues." },
    ],
    engagements: ["One-time platform setup", "Monthly reliability retainer", "Incident postmortem hardening"],
    pillTags: ["AWS", "Azure", "GCP", "Docker", "CI/CD"],
    relatedSlugs: ["custom-software-development", "saas-development", "security-hardening"],
  },
  {
    slug: "erp-crm-development",
    title: "ERP & CRM Development",
    shortTitle: "ERP / CRM",
    metaTitle: "ERP & CRM Customization | Integrations & Portals | Dexton Technology",
    metaDescription:
      "Customization and extensions across CRM/ERP-backed operations—integrations, portals, workflow automation, reports, migrations, API bridges—balanced against upgrade risk and admin usability.",
    keywords:
      "ERP development customization, CRM integration developer, custom business portals, Salesforce HubSpot customization services",
    bullets: ["Integration-first mindset", "Data sync & webhook patterns", "User-friendly admin/report UIs"],
    lead:
      "Off-the-shelf CRM/ERP gets you halfway—then revenue ops hits edge cases. We build the glue layer (and user-facing portals) without turning upgrades into nightmares.",
    audiences: ["SMBs outgrowing default CRM fields", "Ops-heavy B2B", "Agencies building client backends"],
    offerings: [
      { title: "Custom entities & pipelines", detail: "Model your real sales/supply motions instead of spreadsheets." },
      { title: "Integrations & ETL-lite", detail: "Connect accounting, warehouses, ticketing, spreadsheets safely." },
      { title: "Internal portals", detail: "Role-based dashboards and approvals atop your CRM/ERP data." },
    ],
    engagements: ["Discovery + phased milestones", "Integration-only rescue", "Ongoing backlog pod"],
    pillTags: ["REST / GraphQL", "Webhooks", "Workflows", "Reporting", "Migrations"],
    relatedSlugs: ["custom-software-development", "web-development-react", "cloud-devops"],
  },
  {
    slug: "dedicated-software-developers",
    title: "Dedicated Software Developers",
    shortTitle: "Dedicated Team",
    metaTitle: "Hire Dedicated Remote Developers | React, Node, Mobile | Dexton",
    metaDescription:
      "Extend your team with vetted engineers—daily overlap, demos, code review culture, QA-friendly increments, milestone or retainer models. Pilot weeks available before long-term pods.",
    keywords:
      "hire dedicated developers remote, remote react developers, augment development team, agency white label engineering",
    bullets: ["Overlap-friendly communication", "Code review & definition of done", "Pilot engagement option"],
    lead:
      "Staff augmentation fails when accountability is fuzzy—we sell outcomes per sprint (demos + merged increments), not vague ‘hours opened.’",
    audiences: ["Agencies outsourcing white-label builds", "US/UK product teams bridging skill gaps", "Founders bridging to first hire"],
    offerings: [
      { title: "Role-matched pods", detail: "Full-stack, frontend-heavy, mobile, or API-focused squads sized to your backlog." },
      { title: "Process fit", detail: "We adapt to GitHub/GitLab/Jira/Linear—with PR hygiene you can enforce." },
      { title: "Knowledge transfer", detail: "Weekly notes, loom walkthroughs, handoff checkpoints." },
    ],
    engagements: ["2–4 week pilot", "Monthly retainer pod", "Hybrid: fixed scope + bench buffer"],
    pillTags: ["React / Next", "Node.js", "Mobile", "Code review", "Demos"],
    relatedSlugs: ["custom-software-development", "web-development-react", "mobile-app-development"],
  },
  {
    slug: "security-hardening",
    title: "Security & Reliability",
    shortTitle: "Security",
    metaTitle: "Application Security & Hardening | Reviews & Reliability | Dexton",
    metaDescription:
      "Pragmatic hardening for B2B apps—threat modeling basics, auth/session review, dependency hygiene, logging, performance under load, and operational calm under traffic spikes.",
    keywords:
      "application security review, secure software development, performance hardening, GDPR engineering patterns",
    bullets: ["Auth/session posture review", "Dependency & secret hygiene", "Load & failure-mode checks"],
    lead:
      "Not every team needs a full red team on day one—we focus on the fixes that stop common breach paths and painful outages under real traffic.",
    audiences: ["SaaS handling customer data", "Teams pre-due-diligence", "Founders after first security questionnaire"],
    offerings: [
      { title: "Focused reviews", detail: "Top risks first: auth, roles, data exposure, third-party keys." },
      { title: "Hardening sprints", detail: "Fix classes of issues with tests and regression notes." },
      { title: "Reliability passes", detail: "Timeouts, retries, queues, backpressure—boring until it isn’t." },
    ],
    engagements: ["One-time audit + report", "Remediation milestone", "Light ongoing advisory"],
    pillTags: ["AuthZ/AAuth", "Logging", "Dependency updates", "Load testing", "Policies"],
    relatedSlugs: ["cloud-devops", "custom-software-development", "saas-development"],
  },
];

export function getServiceBySlug(slug) {
  return servicesCatalog.find((s) => s.slug === slug);
}

export function getRelatedServices(slugs = []) {
  return slugs.map((s) => getServiceBySlug(s)).filter(Boolean);
}
